// GastosEfectivoPanel.js — gastos pagados en efectivo (gastos_efectivo_manual):
// nunca pasan por el banco, así que movimientos_banco no los ve. El
// agregador los suma dentro de la misma categoría que cualquier gasto de
// banco (Material resta IVA igual que allí).
import React, { useState, useEffect, useCallback } from 'react';
import { authService } from '../../services/authService';
import { CATEGORIAS_GASTO } from './ReglasGastosPanel';

const EMPTY = { id: null, fecha: '', concepto: '', categoria: 'otros_gastos', importe: '', ivaPct: '21' };

function fmtEur(n) { return Number(n || 0).toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' €'; }

export default function GastosEfectivoPanel() {
  const [gastos, setGastos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState(EMPTY);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(() => {
    setLoading(true); setError('');
    fetch('/.netlify/functions/getGastosEfectivo', { headers: { ...authService.authHeader() } })
      .then(r => r.json())
      .then(data => { if (!data.success) { setError(data.error); return; } setGastos(data.gastos); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const handleGuardar = async () => {
    if (!form.fecha || !form.concepto.trim() || !form.importe) return;
    setGuardando(true);
    try {
      const res = await fetch('/.netlify/functions/saveGastoEfectivo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({
          id: form.id || undefined, fecha: form.fecha, concepto: form.concepto, categoria: form.categoria,
          importe: Number(form.importe),
          ivaPct: form.categoria === 'material' ? Number(form.ivaPct || 21) : undefined,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setForm(EMPTY);
      cargar();
    } catch (e) {
      setError(e.message);
    } finally {
      setGuardando(false);
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Eliminar este gasto?')) return;
    try {
      const res = await fetch('/.netlify/functions/saveGastoEfectivo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ accion: 'eliminar', id }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      cargar();
    } catch (e) {
      setError(e.message);
    }
  };

  if (loading) return <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-500" /></div>;

  return (
    <div className="space-y-4">
      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-2 text-sm">{error}</div>}

      <div className="bg-white rounded-2xl border shadow-sm p-5">
        <h3 className="font-bold text-gray-900 text-sm mb-1">Añadir gasto en efectivo</h3>
        <p className="text-[11px] text-gray-400 mb-3">Solo para gastos que de verdad se pagaron en efectivo, sin pasar por el banco.</p>
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-2">
          <input type="date" className="border rounded-lg px-3 py-2 text-sm" value={form.fecha} onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))} />
          <input className="col-span-2 border rounded-lg px-3 py-2 text-sm" placeholder="Concepto" value={form.concepto} onChange={e => setForm(f => ({ ...f, concepto: e.target.value }))} />
          <select className="border rounded-lg px-3 py-2 text-sm" value={form.categoria} onChange={e => setForm(f => ({ ...f, categoria: e.target.value }))}>
            {CATEGORIAS_GASTO.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <input type="number" className="border rounded-lg px-3 py-2 text-sm" placeholder="Importe (€)" value={form.importe} onChange={e => setForm(f => ({ ...f, importe: e.target.value }))} />
          {form.categoria === 'material' ? (
            <input type="number" className="border rounded-lg px-3 py-2 text-sm" placeholder="% IVA" value={form.ivaPct} onChange={e => setForm(f => ({ ...f, ivaPct: e.target.value }))} />
          ) : <span />}
          <button onClick={handleGuardar} disabled={guardando} className="px-3 py-2 bg-red-600 text-white text-xs font-bold rounded-lg hover:bg-red-700 disabled:opacity-60">
            {guardando ? 'Guardando…' : (form.id ? 'Guardar cambios' : 'Añadir')}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b bg-gray-50"><h3 className="font-bold text-sm text-gray-900">Histórico</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-gray-50/60">
                <th className="text-left px-4 py-2 font-bold text-gray-500 uppercase">Fecha</th>
                <th className="text-left px-3 py-2 font-bold text-gray-500 uppercase">Concepto</th>
                <th className="text-left px-3 py-2 font-bold text-gray-500 uppercase">Categoría</th>
                <th className="text-right px-3 py-2 font-bold text-gray-500 uppercase">Importe</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {gastos.map(g => (
                <tr key={g.id}>
                  <td className="px-4 py-2 text-gray-700">{g.fecha}</td>
                  <td className="px-3 py-2 text-gray-600">{g.concepto}</td>
                  <td className="px-3 py-2 text-gray-500">{CATEGORIAS_GASTO.find(c => c.key === g.categoria)?.label || g.categoria}</td>
                  <td className="px-3 py-2 text-right font-semibold">{fmtEur(g.importe)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => setForm({ id: g.id, fecha: g.fecha, concepto: g.concepto, categoria: g.categoria, importe: g.importe, ivaPct: String(g.ivaPct ?? 21) })} className="text-blue-600 hover:underline mr-2">Editar</button>
                    <button onClick={() => handleEliminar(g.id)} className="text-red-600 hover:underline">Eliminar</button>
                  </td>
                </tr>
              ))}
              {gastos.length === 0 && (
                <tr><td colSpan={5} className="text-center py-6 text-gray-400">Sin gastos en efectivo registrados todavía</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
